import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { buildSyncFieldsValidationSchema } from '@/features/menu-management/helpers/buildSyncFieldsValidationSchema'
import {
  AutoTranslateButton,
  AutoTranslateFormEffect
} from '@/features/menu-management/helpers/AutoTranslateFormEffect'
import { useSelectedLanguagesQuery } from '../hooks/useSelectedLanguagesQuery'
import { useDishCategoriesQuery } from '../hooks/useDishCategoriesQuery'
import { MENU_TYPE_HOURS_ENABLED } from '../config'

export const useMenuTypeFormConfig = () => {
  const { t } = useTranslation()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesOptions } =
    useSelectedLanguagesQuery(currentLanguage)

  const filteredSelectedLanguagesOptions = selectedLanguagesOptions.filter(
    selectedLanguage => selectedLanguage.value !== defaultLanguageOption.value
  )

  const { dishCategoriesOptions } = useDishCategoriesQuery()

  // Hidden until the backend can store it, see SidebarAccordion/config.ts.
  const hoursFields = MENU_TYPE_HOURS_ENABLED
    ? [
        {
          name: 'hoursDescription',
          type: 'label',
          label: t('SIDEBAR_ACCORDION.MENU_TYPE.HOURS_DESCRIPTION')
        },
        {
          type: 'input-group',
          inputs: [
            {
              name: 'activeFrom',
              type: 'time',
              // Both start empty, and an unshrunk label inside an empty clock
              // field reads as placeholder text rather than as the field's name.
              focused: true,
              label: t('SIDEBAR_ACCORDION.MENU_TYPE.ACTIVE_FROM'),
              // Every minute, not the picker's default quarters: a menu type
              // starts when the kitchen says it does, and 11:30 is as ordinary
              // a lunch start as 11:00. The roster keeps its 15-minute step,
              // which is the granularity shifts are dragged in.
              minutesStep: 1
            },
            {
              name: 'activeTo',
              type: 'time',
              // Both start empty, and an unshrunk label inside an empty clock
              // field reads as placeholder text rather than as the field's name.
              focused: true,
              label: t('SIDEBAR_ACCORDION.MENU_TYPE.ACTIVE_TO'),
              minutesStep: 1
            }
          ]
        }
      ]
    : []

  const formConfig = {
    id: `menu-type-form`,
    FormEffects: <AutoTranslateFormEffect />,
    // Rendered by the modal in its action row rather than as a form field:
    // a translation helper belongs beside submit and cancel, not wedged
    // between the form's own inputs.
    footerAction: (
      <AutoTranslateButton
        defaultLanguage={defaultLanguageOption?.value || 'en'}
        allLanguages={selectedLanguagesOptions.map(o => o.value as string)}
        syncFieldNames={['translations']}
      />
    ),
    fields: [
      /*
        Not optional on the backend: `MenuTypeDto.categoryIds` carries `@IsArray`
        with no `@IsOptional`, so a payload without it is rejected outright. An
        empty selection is fine, an absent one is a 400.

        First, for the same reason the category form asks for its sizes first:
        what the record covers before what it is called.
      */
      {
        name: 'categories',
        label: t('SIDEBAR_ACCORDION.MENU_TYPE.CATEGORIES'),
        placeholder: t('SIDEBAR_ACCORDION.MENU_TYPE.CATEGORIES_PLACEHOLDER'),
        focused: true,
        type: 'multi-select',
        options: dishCategoriesOptions
      },
      ...hoursFields,
      // The language the rest are translated from, lifted out of the group and
      // above it, as the category and size forms do. `required` is only the
      // asterisk here: the schema below already requires every language.
      {
        name: `translations.${defaultLanguageOption?.value}`,
        label: defaultLanguageOption?.label,
        focused: true,
        type: 'text',
        required: true
      },
      {
        name: 'translations',
        type: 'input-group',
        inputGroupDirection: 'column',
        // One field per enabled language: without its own scroll box the group
        // makes the dialog taller than the viewport.
        scrollable: true,
        inputs: [
          ...filteredSelectedLanguagesOptions.map(language => ({
            name: language.value,
            label: language.label,
            focused: true,
            type: 'text'
          }))
        ]
      }
    ],
    validationSchema: z
      .object({
        translations: buildSyncFieldsValidationSchema(selectedLanguagesOptions),
        categories: z
          .array(
            z.object({
              // Display-only, and a category with no translation in the default
              // language has none; requiring it would block the save over
              // something the user cannot fix from this form.
              label: z.string().optional(),
              value: z.string()
            })
          )
          .optional(),
        ...(MENU_TYPE_HOURS_ENABLED
          ? {
              activeFrom: z.string().optional(),
              activeTo: z.string().optional()
            }
          : {})
      })
      // The two travel as a pair. Not "end after start": a late menu of
      // 22:00–02:00 crosses midnight, which is legitimate.
      .superRefine((value, ctx) => {
        if (!MENU_TYPE_HOURS_ENABLED) return
        const { activeFrom, activeTo } = value as {
          activeFrom?: string
          activeTo?: string
        }
        if (!activeFrom === !activeTo) return
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [activeFrom ? 'activeTo' : 'activeFrom'],
          message: t('SIDEBAR_ACCORDION.MENU_TYPE.HOURS_INCOMPLETE')
        })
      })
  }
  return formConfig
}
